import { api } from './index'

// camelCase domain types (the API returns snake_case; we map explicitly below).
export type InsightsLabV2Report = {
  id: number
  lookerFolderId: string
  title: string
  description?: string
  categoryName?: string
  recommendedPosition?: number
}

export type InsightsLabV2Favorite = {
  id: number
  lookerReportId: number
  name: string
  description?: string
  filterState: Record<string, string>
  position?: number
  reportTitle?: string
}

export type FavoriteInput = {
  lookerReportId: number
  name: string
  description?: string
  filterState?: Record<string, string>
}

type RawReport = {
  id: number
  looker_folder_id: string
  title: string
  description?: string
  category_name?: string
  recommended_position?: number
}

type RawFavorite = {
  id: number
  looker_report_id: number
  name: string
  description?: string
  filter_state?: Record<string, string>
  position?: number
  report_title?: string
}

export const toReport = (r: RawReport): InsightsLabV2Report => ({
  id: r.id,
  lookerFolderId: r.looker_folder_id,
  title: r.title,
  description: r.description,
  categoryName: r.category_name,
  recommendedPosition: r.recommended_position,
})

export const toFavorite = (f: RawFavorite): InsightsLabV2Favorite => ({
  id: f.id,
  lookerReportId: f.looker_report_id,
  name: f.name,
  description: f.description,
  filterState: f.filter_state ?? {},
  position: f.position,
  reportTitle: f.report_title,
})

// Raw Looker wire shape for Cookieless Embedding (Insights Lab v2 only).
// Deliberately NOT camelCased like the rest of this file: @looker/embed-sdk
// round-trips these exact snake_case fields to Looker's iframe, so remapping
// them would break the SDK's internal contract.
export type RawCookielessSessionAcquireResponse = {
  authentication_token: string
  authentication_token_ttl: number
  navigation_token: string
  navigation_token_ttl: number
  api_token: string
  api_token_ttl: number
  session_reference_token: string
  session_reference_token_ttl: number
  looker_dashboard_id: string
}

export type CookielessTokensRequest = {
  session_reference_token: string
  navigation_token?: string
  api_token?: string
}

export type RawCookielessTokensResponse = {
  session_reference_token: string
  navigation_token: string
  navigation_token_ttl: number
  api_token: string
  api_token_ttl: number
  session_reference_token_ttl: number
}

// Base URL already includes /merchants/:current_merchant_id (see api/utils.ts
// buildBaseUrl), so paths are relative to the merchant scope.
export const insightsLabV2Api = api.injectEndpoints({
  endpoints: builder => ({
    getInsightsLabV2Reports: builder.query<InsightsLabV2Report[], void>({
      query: () => 'insights_lab_v2/reports',
      transformResponse: (raw: RawReport[]) => raw.map(toReport),
      providesTags: ['InsightsLabV2Reports'],
    }),
    getInsightsLabV2RecommendedReports: builder.query<
      InsightsLabV2Report[],
      void
    >({
      query: () => 'insights_lab_v2/recommended_reports',
      transformResponse: (raw: RawReport[]) => raw.map(toReport),
      providesTags: ['InsightsLabV2RecommendedReports'],
    }),
    getInsightsLabV2Favorites: builder.query<InsightsLabV2Favorite[], void>({
      query: () => 'insights_lab_v2/favorites',
      transformResponse: (raw: RawFavorite[]) => raw.map(toFavorite),
      providesTags: ['InsightsLabV2Favorites'],
    }),
    createInsightsLabV2Favorite: builder.mutation<
      InsightsLabV2Favorite,
      FavoriteInput
    >({
      query: ({ lookerReportId, name, description, filterState }) => ({
        url: 'insights_lab_v2/favorites',
        method: 'POST',
        body: {
          looker_report_id: lookerReportId,
          name,
          description,
          filter_state: filterState ?? {},
        },
      }),
      transformResponse: (raw: RawFavorite) => toFavorite(raw),
      invalidatesTags: ['InsightsLabV2Favorites'],
    }),
    deleteInsightsLabV2Favorite: builder.mutation<void, number>({
      query: id => ({
        url: `insights_lab_v2/favorites/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InsightsLabV2Favorites'],
    }),
    acquireInsightsLabV2CookielessSession: builder.query<
      RawCookielessSessionAcquireResponse,
      { reportId: number }
    >({
      query: ({ reportId }) =>
        `insights_lab_v2/reports/${reportId}/cookieless_session`,
      // Tokens are short-lived; don't serve a stale cached acquire response
      // on reopen.
      keepUnusedDataFor: 0,
    }),
    generateInsightsLabV2CookielessTokens: builder.mutation<
      RawCookielessTokensResponse,
      CookielessTokensRequest
    >({
      query: body => ({
        url: 'insights_lab_v2/cookieless_tokens',
        method: 'PUT',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetInsightsLabV2ReportsQuery,
  useGetInsightsLabV2RecommendedReportsQuery,
  useGetInsightsLabV2FavoritesQuery,
  useCreateInsightsLabV2FavoriteMutation,
  useDeleteInsightsLabV2FavoriteMutation,
  useLazyAcquireInsightsLabV2CookielessSessionQuery,
  useGenerateInsightsLabV2CookielessTokensMutation,
} = insightsLabV2Api
